import { CalendarIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CalendarDateSerialized,
    ClientId,
    CommunityId,
} from "@sp-crm/core";
import { TourStatus } from "generated/graphql";
import React, { useCallback, useState } from "react";
import { TourData } from "../community-search/types";
import { TourEditor } from "./tour-editor";

interface ToursSectionProps {
    tours: TourData[];
    clientId: ClientId;
    communityId: CommunityId;
    onToursChanged: () => void;
}

const statusBadgeColors: Record<TourStatus, string> = {
    [TourStatus.Scheduled]: "bg-blue-100 text-blue-800",
    [TourStatus.Completed]: "bg-green-100 text-green-800",
    [TourStatus.Cancelled]: "bg-gray-100 text-gray-600",
};

const formatDate = (dateValue: CalendarDateSerialized): string => {
    const parsed = CalendarDate.parse(dateValue as unknown as string);
    if (!parsed.hasValue) {
        return "Unknown date";
    }
    const calDate = parsed.get();
    return calDate.toDate().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const formatTime = (timeStr: string | null): string => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return ` at ${hour12}:${minutes} ${ampm}`;
};

export const ToursSection: React.FC<ToursSectionProps> = props => {
    const { tours, clientId, communityId, onToursChanged } = props;
    const [editingTour, setEditingTour] = useState<TourData | null | "new">(null);

    const handleAddTour = useCallback(() => {
        setEditingTour("new");
    }, []);

    const handleEditTour = useCallback((tour: TourData) => {
        setEditingTour(tour);
    }, []);

    const handleClose = useCallback(() => {
        setEditingTour(null);
    }, []);

    const handleSaved = useCallback(() => {
        onToursChanged();
    }, [onToursChanged]);

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-1">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Tours</span>
                </h4>
                <button
                    onClick={handleAddTour}
                    className="flex items-center space-x-1 text-sm text-brand-600 hover:text-brand-800">
                    <PlusIcon className="h-4 w-4" />
                    <span>Add Tour</span>
                </button>
            </div>

            {tours.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                    {tours.map(tour => (
                        <li key={tour.id}>
                            <button
                                onClick={() => handleEditTour(tour)}
                                className="w-full text-left p-2 hover:bg-gray-50 transition-colors cursor-pointer">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-900">
                                            {formatDate(tour.tourDate)}
                                            {formatTime(tour.tourTime)}
                                        </span>
                                    </div>
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeColors[tour.status]}`}>
                                        {tour.status}
                                    </span>
                                </div>
                                {tour.familyNotes ? (
                                    <p className="text-xs text-gray-500 mt-1 truncate">
                                        {tour.familyNotes}
                                    </p>
                                ) : null}
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-gray-500">No tours scheduled</p>
            )}

            {editingTour !== null ? (
                <TourEditor
                    tour={editingTour === "new" ? null : editingTour}
                    clientId={clientId}
                    communityId={communityId}
                    onSaved={handleSaved}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
};
